import React from 'react'
import {
  IconButton,
  Tooltip,
  Box
} from '@mui/material'
import { Refresh as RefreshIcon } from '@mui/icons-material'
import { keyframes } from '@mui/system'
import { useAuth } from '../contexts/AuthContext'
import { formatDistanceToNow } from 'date-fns'
import useSyncTrigger from '../hooks/useSyncTrigger'

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

interface RefreshDataButtonProps {
  fpCode: string
  lastSyncDate?: string
  franchiseData: any
  onSyncStarted?: () => void
}

const IDLE_TOOLTIP = "Sync latest data - updates the Inspector Tool's data snapshot from connected systems."

const RefreshDataButton: React.FC<RefreshDataButtonProps> = ({
  fpCode,
  lastSyncDate,
  franchiseData,
  onSyncStarted
}) => {
  const { user } = useAuth()
  const { triggerSync, loading, activeSyncs, checking } = useSyncTrigger({
    fpCode,
    franchiseData,
    onSyncStarted,
  })

  const isLastSyncOlderThan24Hours = () => {
    if (!lastSyncDate) return true
    const lastSync = new Date(lastSyncDate)
    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)
    return lastSync < twentyFourHoursAgo
  }

  const shouldShowButton = () => {
    if (!user) return false
    if (checking) return false
    if (user.role === 'FP_USER') {
      return isLastSyncOlderThan24Hours() || activeSyncs.length > 0
    }
    return true
  }

  if (!shouldShowButton()) {
    return null
  }

  const isButtonDisabled = activeSyncs.length > 0 || loading
  const isSyncing = activeSyncs.length > 0 || loading

  const getTooltipText = () => {
    if (activeSyncs.length > 0) {
      return `Sync in progress: ${activeSyncs.map(s => s.sync_system).join(', ')}`
    }
    if (lastSyncDate) {
      const timeAgo = formatDistanceToNow(new Date(lastSyncDate), { addSuffix: true })
      return `Sync latest data - last synced ${timeAgo}.`
    }
    return IDLE_TOOLTIP
  }

  return (
    <Tooltip title={getTooltipText()}>
      <Box>
        <IconButton
          size="small"
          onClick={triggerSync}
          disabled={isButtonDisabled}
          aria-label={IDLE_TOOLTIP}
          sx={{
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            },
            '&.Mui-disabled': {
              color: isSyncing ? 'white' : 'rgba(255, 255, 255, 0.4)'
            }
          }}
        >
          <RefreshIcon
            sx={{
              animation: isSyncing ? `${spin} 1.5s linear infinite` : 'none'
            }}
          />
        </IconButton>
      </Box>
    </Tooltip>
  )
}

export default RefreshDataButton
